import type { LegalDocument, LegalLanguage } from './legal-document.ts'
import { CONTACT_EMAIL } from './privacy-content.ts'

/** Bump whenever a clause changes — it is what the page shows the reader. */
const EFFECTIVE_DATE = '2026-09-22'

const ID: LegalDocument = {
  title: 'Syarat & Ketentuan',
  subtitle:
    'Aturan pemakaian YT Clip AI: bagaimana kredit bekerja, apa yang boleh dan tidak boleh dilakukan, dan batas tanggung jawab kami.',
  effectiveDate: EFFECTIVE_DATE,
  effectiveLabel: 'Berlaku sejak',
  summary:
    'Yang paling penting untuk diketahui: kredit dibeli dalam USD dan dibayar dalam rupiah lewat QRIS, saldo dipotong per permintaan sesuai pemakaian nyata, kredit yang sudah dibeli tidak dapat diuangkan atau dikembalikan, dan API key kamu adalah tanggung jawabmu sendiri.',
  tocLabel: 'Isi ketentuan',
  sections: [
    {
      id: 'penerimaan',
      heading: 'Penerimaan syarat',
      blocks: [
        {
          kind: 'p',
          text: 'Dengan membuat akun, melakukan top-up, memanggil API, atau memakai aplikasi desktop YT Clip AI, kamu menyatakan telah membaca, memahami, dan terikat pada syarat ini beserta Kebijakan Privasi kami. Bila kamu tidak setuju, jangan memakai layanan ini.',
        },
        {
          kind: 'p',
          text: 'Bila kamu memakai layanan ini atas nama perusahaan atau organisasi, kamu menyatakan berwenang mengikat pihak tersebut pada syarat ini.',
        },
      ],
    },
    {
      id: 'layanan',
      heading: 'Tentang layanan',
      blocks: [
        {
          kind: 'p',
          text: 'YT Clip AI menyediakan satu saldo kredit yang bisa dipakai untuk mengakses berbagai model AI melalui satu API. Kami adalah perantara: model dijalankan oleh penyedia pihak ketiga, dan kami meneruskan permintaanmu kepada mereka lalu memotong saldomu sesuai biaya yang timbul.',
        },
        {
          kind: 'p',
          text: 'Daftar model, kemampuan, dan tarifnya dapat berubah sewaktu-waktu mengikuti perubahan dari penyedia model.',
        },
      ],
    },
    {
      id: 'akun',
      heading: 'Akun',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Login hanya melalui akun Google. Kamu bertanggung jawab menjaga keamanan akun Google tersebut.',
            'Satu orang atau satu badan usaha hanya boleh memiliki satu akun, kecuali kami menyetujui sebaliknya secara tertulis.',
            'Data yang kamu berikan harus benar dan terkini.',
            'Kamu bertanggung jawab atas semua aktivitas yang terjadi di akunmu, termasuk pemakaian oleh orang lain yang kamu beri akses.',
            'Akun dan saldo di dalamnya tidak boleh diperjualbelikan atau dipindahtangankan tanpa persetujuan tertulis dari kami.',
          ],
        },
      ],
    },
    {
      id: 'kredit-topup',
      heading: 'Kredit dan top-up',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Saldo dihitung dalam dolar Amerika (USD) dan dibayar dalam rupiah (IDR) melalui QRIS.',
            'Jumlah rupiah yang kamu bayar dihitung dari nominal USD dikali kurs yang berlaku, ditambah biaya platform. Kurs, besaran biaya, serta batas minimum dan maksimum top-up ditampilkan di dashboard sebelum kamu membayar, dan dapat berubah sewaktu-waktu. Yang mengikat adalah angka yang ditampilkan pada saat transaksi dibuat.',
            'Kode QRIS memiliki masa berlaku. Setelah lewat, kode itu tidak bisa dipakai lagi dan kamu perlu membuat permintaan top-up baru.',
            'Saldo ditambahkan ke akunmu setelah pembayaran dikonfirmasi oleh penyedia pembayaran. Konfirmasi ini biasanya berlangsung cepat, tetapi kami tidak bisa menjamin waktunya.',
            'Bila kamu sudah membayar tetapi saldo tidak masuk, hubungi kami dengan menyertakan bukti pembayaran dan nomor referensi transaksi.',
          ],
        },
        {
          kind: 'p',
          text: 'Kredit YT Clip AI adalah saldo prabayar untuk memakai layanan ini saja. Kredit bukan uang elektronik, bukan simpanan, dan bukan alat pembayaran; kredit tidak menghasilkan bunga dan tidak dapat dicairkan menjadi uang tunai.',
        },
      ],
    },
    {
      id: 'tarif-pemakaian',
      heading: 'Tarif dan pemotongan saldo',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Setiap permintaan ke model AI memotong saldo sesuai pemakaian nyata — jumlah token masukan dan keluaran dikali tarif model yang bersangkutan.',
            'Biaya dicatat sampai enam angka di belakang koma dan dapat kamu periksa satu per satu di halaman Pemakaian.',
            'Permintaan yang gagal karena kesalahan dari sisi kami atau penyedia model tidak akan dibebankan. Permintaan yang tetap diproses model dan dikenakan biaya oleh penyedia akan tetap memotong saldo, meskipun hasilnya tidak sesuai harapanmu.',
            'Bila saldo habis, permintaan berikutnya akan ditolak sampai kamu melakukan top-up lagi.',
            'Kamu dapat memasang batas belanja pada setiap API key untuk membatasi pemakaian.',
          ],
        },
      ],
    },
    {
      id: 'refund',
      heading: 'Pengembalian dana',
      blocks: [
        {
          kind: 'p',
          text: 'Karena kredit langsung dapat dipakai begitu masuk, semua pembelian kredit bersifat final dan tidak dapat dikembalikan atau diuangkan.',
        },
        {
          kind: 'p',
          text: 'Pengecualian hanya berlaku untuk kesalahan sistem yang dapat dibuktikan, misalnya pembayaran terdebet ganda, saldo tidak masuk padahal pembayaran berhasil, atau pemotongan saldo yang keliru karena kesalahan teknis kami. Dalam kasus seperti itu kami akan mengembalikan nilainya dalam bentuk kredit ke akunmu setelah pemeriksaan. Laporkan dalam waktu 30 hari sejak transaksi terjadi, dengan menyertakan bukti pembayaran.',
        },
        {
          kind: 'p',
          text: 'Bila akunmu ditutup — baik oleh kamu sendiri maupun karena pelanggaran syarat ini — sisa saldo hangus dan tidak dapat diuangkan.',
        },
      ],
    },
    {
      id: 'api-key',
      heading: 'API key dan keamanan',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Secret API key hanya ditampilkan satu kali saat dibuat. Simpan di tempat yang aman; kami tidak dapat menampilkannya kembali.',
            'Jangan menaruh API key di kode yang dipublikasikan, di repositori publik, atau di aplikasi sisi klien yang bisa dibaca pengguna.',
            'Semua pemakaian melalui API key kamu dianggap berasal darimu dan tetap memotong saldomu, termasuk bila key itu bocor.',
            'Bila kamu menduga sebuah key bocor, segera cabut key tersebut dari dashboard dan buat yang baru.',
            'Kami dapat mencabut atau membatasi sebuah key tanpa pemberitahuan sebelumnya bila key itu terindikasi disalahgunakan atau mengancam kestabilan layanan.',
          ],
        },
      ],
    },
    {
      id: 'penggunaan-terlarang',
      heading: 'Penggunaan yang dilarang',
      blocks: [
        { kind: 'p', text: 'Kamu dilarang memakai layanan ini untuk:' },
        {
          kind: 'ul',
          items: [
            'Kegiatan yang melanggar hukum Republik Indonesia atau hukum yang berlaku di wilayahmu.',
            'Melanggar hak kekayaan intelektual, hak privasi, atau hak hukum lain milik orang lain.',
            'Membuat atau menyebarkan malware, melakukan peretasan, atau berusaha mendapatkan akses tidak sah ke sistem mana pun, termasuk sistem kami.',
            'Membuat konten yang bersifat pelecehan seksual terhadap anak, ujaran kebencian, ancaman kekerasan, atau materi lain yang dilarang oleh kebijakan penyedia model yang kamu pakai.',
            'Penipuan, spam, manipulasi pasar, atau kampanye disinformasi.',
            'Mengakali batas pemakaian, membuat banyak akun untuk menghindari pembatasan, atau menguji ketahanan sistem kami tanpa izin tertulis.',
            'Menjual kembali akses ke layanan ini sebagai produkmu sendiri tanpa perjanjian tertulis dengan kami.',
            'Melakukan rekayasa balik, membongkar, atau mencoba mengekstrak kode sumber dari layanan maupun aplikasi desktop kami.',
          ],
        },
        {
          kind: 'p',
          text: 'Selain syarat ini, kamu juga terikat pada kebijakan penggunaan masing-masing penyedia model yang permintaanmu diteruskan kepadanya.',
        },
      ],
    },
    {
      id: 'keluaran-ai',
      heading: 'Keluaran AI dan hak atas konten',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Isi prompt yang kamu kirim tetap menjadi milikmu, dan kamu bertanggung jawab memastikan kamu berhak mengirimkannya.',
            'Keluaran model dihasilkan secara otomatis. Keluaran itu bisa keliru, bias, sudah tidak berlaku, atau menyerupai keluaran yang diterima pengguna lain. Kami tidak menjamin keakuratan, kelayakan, maupun orisinalitasnya.',
            'Kamu bertanggung jawab penuh memverifikasi keluaran sebelum memakainya, terutama untuk keperluan medis, hukum, keuangan, atau keselamatan. Keluaran model bukan nasihat profesional.',
            'Hak atas keluaran model tunduk pada ketentuan penyedia model yang bersangkutan.',
          ],
        },
      ],
    },
    {
      id: 'aplikasi-desktop',
      heading: 'Aplikasi desktop dan token perangkat',
      blocks: [
        {
          kind: 'p',
          text: 'Aplikasi desktop dapat dipakai tanpa registrasi. Saldo melekat pada token perangkat yang diterima aplikasi saat pertama dijalankan, bukan pada sebuah akun. Token itu hanya ditampilkan satu kali.',
        },
        {
          kind: 'p',
          text: 'Karena tidak ada akun untuk dipulihkan, token yang hilang berarti saldo yang hilang, dan kami tidak dapat memulihkannya. Kami juga dapat mewajibkan versi aplikasi minimum; versi yang terlalu lama dapat berhenti berfungsi sampai diperbarui.',
        },
      ],
    },
    {
      id: 'membership',
      heading: 'Membership Gold dan VIP support',
      blocks: [
        {
          kind: 'p',
          text: 'Akun yang total top-up seumur hidupnya mencapai ambang yang kami tetapkan berstatus Gold dan berhak mengakses grup dukungan VIP di Telegram. Ambang, manfaat, dan bentuk dukungannya dapat kami ubah sewaktu-waktu, dan akses grup dapat dicabut bila kamu melanggar aturan grup atau syarat ini.',
        },
      ],
    },
    {
      id: 'ketersediaan',
      heading: 'Ketersediaan layanan',
      blocks: [
        {
          kind: 'p',
          text: 'Layanan disediakan apa adanya, tanpa jaminan ketersediaan tertentu. Kami dapat melakukan pemeliharaan, mengubah fitur, menambah atau menghentikan model, dan menyesuaikan tarif tanpa pemberitahuan sebelumnya.',
        },
        {
          kind: 'p',
          text: 'Gangguan juga dapat berasal dari penyedia model, penyedia pembayaran, atau jaringan di luar kendali kami. Kami akan berupaya wajar memulihkan layanan secepatnya, tetapi tidak menjamin layanan bebas gangguan atau bebas kesalahan.',
        },
      ],
    },
    {
      id: 'penangguhan',
      heading: 'Penangguhan dan penutupan akun',
      blocks: [
        {
          kind: 'p',
          text: 'Kami dapat menangguhkan atau menutup akunmu, dengan atau tanpa pemberitahuan sebelumnya, bila kamu melanggar syarat ini, bila ada indikasi penipuan atau penyalahgunaan pembayaran, atau bila diwajibkan oleh hukum.',
        },
        {
          kind: 'p',
          text: 'Kamu dapat berhenti memakai layanan kapan saja dan meminta penutupan akun. Dalam kedua kasus tersebut, sisa saldo hangus sebagaimana diatur pada bagian pengembalian dana.',
        },
      ],
    },
    {
      id: 'tanggung-jawab',
      heading: 'Batasan tanggung jawab',
      blocks: [
        {
          kind: 'p',
          text: 'Sepanjang diizinkan hukum yang berlaku, YT Clip AI tidak bertanggung jawab atas kehilangan keuntungan, kehilangan data, kerugian tidak langsung, insidental, atau konsekuensial yang timbul dari pemakaian atau ketidakmampuan memakai layanan ini.',
        },
        {
          kind: 'p',
          text: 'Total tanggung jawab kami atas klaim apa pun terkait layanan ini dibatasi pada jumlah yang kamu bayarkan kepada kami dalam tiga bulan terakhir sebelum klaim tersebut timbul.',
        },
        {
          kind: 'p',
          text: 'Kamu setuju membebaskan kami dari tuntutan pihak ketiga yang muncul akibat pemakaian layanan oleh kamu yang melanggar syarat ini atau melanggar hukum.',
        },
      ],
    },
    {
      id: 'perubahan-syarat',
      heading: 'Perubahan syarat',
      blocks: [
        {
          kind: 'p',
          text: 'Syarat ini dapat kami ubah sewaktu-waktu. Tanggal berlaku di bagian atas halaman menunjukkan versi terbaru. Bila perubahannya berdampak besar, kami akan memberitahu lewat email atau pengumuman di dashboard. Melanjutkan pemakaian setelah perubahan berlaku berarti kamu menerima versi yang baru.',
        },
      ],
    },
    {
      id: 'hukum',
      heading: 'Hukum yang berlaku',
      blocks: [
        {
          kind: 'p',
          text: 'Syarat ini tunduk pada hukum Republik Indonesia. Setiap perselisihan akan diupayakan diselesaikan secara musyawarah terlebih dahulu; bila tidak tercapai kesepakatan, perselisihan diselesaikan melalui pengadilan yang berwenang di Indonesia.',
        },
        {
          kind: 'p',
          text: 'Bila ada ketentuan dalam syarat ini yang dinyatakan tidak berlaku, ketentuan lainnya tetap berlaku sepenuhnya.',
        },
      ],
    },
    {
      id: 'kontak',
      heading: 'Kontak',
      blocks: [
        {
          kind: 'p',
          text: `Pertanyaan tentang syarat ini bisa dikirim ke ${CONTACT_EMAIL}.`,
        },
      ],
    },
  ],
}

const EN: LegalDocument = {
  title: 'Terms & Conditions',
  subtitle:
    'The rules for using YT Clip AI: how credit works, what is and is not allowed, and the limits of our liability.',
  effectiveDate: EFFECTIVE_DATE,
  effectiveLabel: 'Effective from',
  summary:
    'The parts that matter most: credit is priced in USD and paid in rupiah via QRIS, your balance is charged per request based on actual usage, purchased credit cannot be cashed out or refunded, and your API keys are your own responsibility.',
  tocLabel: 'Contents',
  sections: [
    {
      id: 'penerimaan',
      heading: 'Acceptance of these terms',
      blocks: [
        {
          kind: 'p',
          text: 'By creating an account, topping up, calling the API, or using the YT Clip AI desktop app, you confirm that you have read, understood, and are bound by these terms together with our Privacy Policy. If you do not agree, do not use the service.',
        },
        {
          kind: 'p',
          text: 'If you use the service on behalf of a company or organisation, you confirm that you are authorised to bind that party to these terms.',
        },
      ],
    },
    {
      id: 'layanan',
      heading: 'About the service',
      blocks: [
        {
          kind: 'p',
          text: 'YT Clip AI provides a single credit balance you can spend across multiple AI models through one API. We are an intermediary: the models are run by third-party providers, and we forward your requests to them and charge your balance for what they cost.',
        },
        {
          kind: 'p',
          text: 'The catalog of models, their capabilities, and their rates may change at any time as the providers change theirs.',
        },
      ],
    },
    {
      id: 'akun',
      heading: 'Your account',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Google is the only way to sign in. Keeping that Google account secure is your responsibility.',
            'One person or legal entity may hold one account, unless we agree otherwise in writing.',
            'The details you provide must be accurate and current.',
            'You are responsible for everything that happens on your account, including use by anyone you grant access to.',
            'Accounts and the balance in them may not be sold or transferred without our written consent.',
          ],
        },
      ],
    },
    {
      id: 'kredit-topup',
      heading: 'Credit and top-ups',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Your balance is denominated in US dollars (USD) and paid in rupiah (IDR) via QRIS.',
            'The rupiah amount is the USD amount multiplied by the prevailing exchange rate, plus a platform fee. The rate, the fee, and the minimum and maximum top-up amounts are shown in the dashboard before you pay and may change at any time. The figures shown when a transaction is created are the ones that bind.',
            'A QRIS code has an expiry. Once it passes, the code can no longer be used and you will need to create a new top-up request.',
            'Credit is added to your account once the payment provider confirms the payment. Confirmation is usually quick, but we cannot guarantee how long it takes.',
            'If you have paid but the balance has not arrived, contact us with your proof of payment and the transaction reference.',
          ],
        },
        {
          kind: 'p',
          text: 'YT Clip AI credit is a prepaid balance usable only within this service. It is not electronic money, not a deposit, and not a means of payment; it earns no interest and cannot be withdrawn as cash.',
        },
      ],
    },
    {
      id: 'tarif-pemakaian',
      heading: 'Rates and charging',
      blocks: [
        {
          kind: 'ul',
          items: [
            'Every request to an AI model charges your balance for actual usage — input and output token counts multiplied by that model rate.',
            'Charges are recorded to six decimal places and can be inspected request by request on the Usage page.',
            'Requests that fail because of an error on our side or the provider side are not charged. Requests the model did process and did bill for will still be charged, even if the result was not what you hoped for.',
            'When your balance runs out, further requests are rejected until you top up again.',
            'You can set a spend limit on each API key to cap its usage.',
          ],
        },
      ],
    },
    {
      id: 'refund',
      heading: 'Refunds',
      blocks: [
        {
          kind: 'p',
          text: 'Because credit becomes usable the moment it lands, all credit purchases are final and cannot be refunded or cashed out.',
        },
        {
          kind: 'p',
          text: 'The only exception is a demonstrable system error — for example a double debit, a successful payment whose credit never arrived, or an incorrect charge caused by a technical fault on our side. In those cases we will restore the value as credit to your account after investigating. Report it within 30 days of the transaction, with proof of payment.',
        },
        {
          kind: 'p',
          text: 'If your account is closed — whether by you or for breaching these terms — any remaining balance is forfeited and cannot be cashed out.',
        },
      ],
    },
    {
      id: 'api-key',
      heading: 'API keys and security',
      blocks: [
        {
          kind: 'ul',
          items: [
            'An API key secret is shown only once, at creation. Store it somewhere safe; we cannot show it to you again.',
            'Do not put an API key in published code, a public repository, or a client-side application where users can read it.',
            'All usage through your API keys is treated as yours and charged to your balance, including usage that follows a leaked key.',
            'If you suspect a key has leaked, revoke it from the dashboard immediately and issue a new one.',
            'We may revoke or restrict a key without prior notice if it shows signs of abuse or threatens the stability of the service.',
          ],
        },
      ],
    },
    {
      id: 'penggunaan-terlarang',
      heading: 'Prohibited use',
      blocks: [
        { kind: 'p', text: 'You may not use the service to:' },
        {
          kind: 'ul',
          items: [
            'Carry out activity that breaks Indonesian law or the law of your own jurisdiction.',
            'Infringe intellectual property, privacy, or other legal rights of others.',
            'Create or distribute malware, attempt intrusion, or seek unauthorised access to any system, including ours.',
            'Generate child sexual abuse material, hate speech, threats of violence, or other material prohibited by the usage policy of the model provider you are calling.',
            'Commit fraud, send spam, manipulate markets, or run disinformation campaigns.',
            'Circumvent usage limits, create multiple accounts to evade restrictions, or stress-test our systems without written permission.',
            'Resell access to this service as your own product without a written agreement with us.',
            'Reverse engineer, decompile, or attempt to extract source code from the service or our desktop app.',
          ],
        },
        {
          kind: 'p',
          text: 'Beyond these terms, you are also bound by the usage policy of each model provider your requests are forwarded to.',
        },
      ],
    },
    {
      id: 'keluaran-ai',
      heading: 'AI output and content rights',
      blocks: [
        {
          kind: 'ul',
          items: [
            'The prompts you send remain yours, and it is your responsibility to make sure you have the right to send them.',
            'Model output is generated automatically. It can be wrong, biased, out of date, or similar to output another user received. We make no guarantee of accuracy, fitness for purpose, or originality.',
            'Verifying output before you rely on it is entirely your responsibility, particularly for medical, legal, financial, or safety-related purposes. Model output is not professional advice.',
            'Rights in model output are governed by the terms of the relevant model provider.',
          ],
        },
      ],
    },
    {
      id: 'aplikasi-desktop',
      heading: 'Desktop app and device tokens',
      blocks: [
        {
          kind: 'p',
          text: 'The desktop app can be used without registering. The balance is attached to the device token the app receives on first launch, not to an account, and that token is shown exactly once.',
        },
        {
          kind: 'p',
          text: 'Because there is no account to recover, a lost token means a lost balance, and we cannot restore it. We may also require a minimum app version; versions older than that may stop working until they are updated.',
        },
      ],
    },
    {
      id: 'membership',
      heading: 'Gold membership and VIP support',
      blocks: [
        {
          kind: 'p',
          text: 'An account whose lifetime top-ups reach the threshold we set becomes Gold and gains access to the VIP support group on Telegram. The threshold, the benefits, and the form that support takes may change at any time, and group access may be withdrawn if you breach the group rules or these terms.',
        },
      ],
    },
    {
      id: 'ketersediaan',
      heading: 'Service availability',
      blocks: [
        {
          kind: 'p',
          text: 'The service is provided as is, with no guaranteed level of availability. We may carry out maintenance, change features, add or retire models, and adjust rates without prior notice.',
        },
        {
          kind: 'p',
          text: 'Disruption may also originate with a model provider, the payment provider, or networks outside our control. We will make reasonable efforts to restore service promptly, but we do not promise it will be uninterrupted or error-free.',
        },
      ],
    },
    {
      id: 'penangguhan',
      heading: 'Suspension and closure',
      blocks: [
        {
          kind: 'p',
          text: 'We may suspend or close your account, with or without prior notice, if you breach these terms, if there are signs of fraud or payment abuse, or where the law requires it.',
        },
        {
          kind: 'p',
          text: 'You may stop using the service at any time and ask us to close your account. In either case, any remaining balance is forfeited as set out in the refunds section.',
        },
      ],
    },
    {
      id: 'tanggung-jawab',
      heading: 'Limitation of liability',
      blocks: [
        {
          kind: 'p',
          text: 'To the extent permitted by applicable law, YT Clip AI is not liable for lost profits, lost data, or indirect, incidental, or consequential damages arising from your use of, or inability to use, the service.',
        },
        {
          kind: 'p',
          text: 'Our total liability for any claim relating to the service is limited to the amount you paid us in the three months before the claim arose.',
        },
        {
          kind: 'p',
          text: 'You agree to hold us harmless from third-party claims arising out of your use of the service in breach of these terms or of the law.',
        },
      ],
    },
    {
      id: 'perubahan-syarat',
      heading: 'Changes to these terms',
      blocks: [
        {
          kind: 'p',
          text: 'We may change these terms at any time. The effective date at the top of this page reflects the current version. For significant changes we will notify you by email or through a notice in the dashboard. Continuing to use the service after a change takes effect means you accept the new version.',
        },
      ],
    },
    {
      id: 'hukum',
      heading: 'Governing law',
      blocks: [
        {
          kind: 'p',
          text: 'These terms are governed by the laws of the Republic of Indonesia. Any dispute will first be addressed through good-faith discussion; failing agreement, it will be resolved before the competent courts in Indonesia.',
        },
        {
          kind: 'p',
          text: 'If any provision of these terms is held unenforceable, the remaining provisions continue in full force.',
        },
      ],
    },
    {
      id: 'kontak',
      heading: 'Contact',
      blocks: [
        {
          kind: 'p',
          text: `Questions about these terms can be sent to ${CONTACT_EMAIL}.`,
        },
      ],
    },
  ],
}

export const TERMS_AND_CONDITIONS: Record<LegalLanguage, LegalDocument> = { id: ID, en: EN }
